import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { TableUI } from "components/design/TableUI";
import { EditPencil } from "components/icons/EditPencil";
import { DeleteIcon } from "components/icons/delete";
import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import Search from "static/images/Search.svg";
import { AddressAutofill } from "types/Models/addressAutofill";
import { BankAutofill } from "types/Models/bankAutofill";
import { PersonaAutofill } from "types/Models/personaAutofill";

type AutofillItem = PersonaAutofill | BankAutofill | AddressAutofill;

type AutofillTableProps = {
  items: AutofillItem[];
  onDelete: ({ uuid }: { uuid: string }) => void;
  type: "addresses" | "bank_accounts" | "authorized_persons";
  onEdit: ({
    autofill,
    type,
  }: {
    autofill: AutofillItem;
    type: "addresses" | "bank_accounts" | "authorized_persons";
  }) => void;
  isLoading: boolean;
  isSuccess: boolean;
};

const columnHelper = createColumnHelper<AutofillItem>();

export const AutofillTable = ({
  items = [],
  onEdit,
  onDelete,
  type,
  isLoading,
  isSuccess,
}: AutofillTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor("autofill_string", {
        header: "Title",
        size: 80,
        cell: ({ getValue }) => (
          <div className="t-text-body-sm">{getValue()}</div>
        ),
      }),
      columnHelper.display({
        id: "action",
        size: 20,
        cell: ({ row }) => (
          <div className="t-flex t-justify-end t-gap-2">
            <Button
              onClick={() => onEdit({ autofill: row.original, type })}
              customType="icon"
            >
              <EditPencil className="t-size-5 t-text-text-60" />
            </Button>
            <Button onClick={() => onDelete(row.original)} customType="icon">
              <span className="t-text-text-60">
                <DeleteIcon color="currentColor" size="20" stroke="1" />
              </span>
            </Button>
          </div>
        ),
      }),
    ],
    [type, onEdit, onDelete]
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Async.Root
      isLoading={isLoading}
      isEmpty={items.length === 0}
      isSuccess={isSuccess}
    >
      <Async.Empty>
        <div className="t-flex t-h-[31rem] t-flex-col t-items-center t-justify-center t-gap-4 t-text-subtitle-sm t-font-medium t-text-text-30">
          <img src={Search} alt="No autofill found" />
          Look's like you don't have any autofill
        </div>
      </Async.Empty>
      <Async.Success>
        <TableUI table={table} />
      </Async.Success>
    </Async.Root>
  );
};
